/**
 * Seedance 2.x family descriptors (Phase 1, U6): seedance-2.5, seedance-2 ("Pro" in the UI, fal's
 * 2.0 standard tier), seedance-2-fast, seedance-2-mini. BARE `bytedance/` namespace.
 *
 * PURE: `import type` + sibling data only (purity.test). Importable from server/ and src/.
 *
 * Reproduces TODAY's behaviour (P1 is price- and payload-neutral):
 *   editor   server/routes/falai.ts `case 'seedance-2.5' | 'seedance-2' | 'seedance-2-fast' | 'seedance-2-mini'`
 *   headless ProviderService.generateVideoFalAI `model.startsWith('seedance-2')` branch
 *   price    CreditService SEEDANCE_FAL_RATES (explicit 480p/720p table, rate x max(1, ceil(units)))
 *
 *   - duration is a STRING enum on fal. Legacy sends String(min(15, max(4, round(Number(d) || 5)))):
 *     `|| 5` maps 0 / NaN / '' / null to 5, which is why the descriptor default is 5 and 0 is
 *     aliased to 5. 2.5 accepts up to 30 on fal; legacy caps every tier at 15.
 *
 *     R5 — THE BATCH RULE, spelled out in full in fal/kling-video.ts: `default` is what LEGACY
 *     resolves for an absent or blank body duration on the EDITOR surface (the route's own
 *     `duration = 5`, falai.ts:1197) whenever that value is in the model's domain. Here it is
 *     not a free choice: the U0 blank rows (duration '' / null) reach the engine as absent, and
 *     only 5 reproduces the `Number('') || 5` the route bills and renders — which is why this
 *     family has no BLANK pins while kling-3.0 does. The NODE defaults differ (2.5
 *     ships 5, the other three ship 6) and are recorded per tier in `durationDefault` below; a
 *     P2 picker must read them from there, not from `default`.
 *   - BILLING uses the RAW body duration (reserve(units = body.duration)), not the clamped one:
 *     units stage 'raw'. A 20 s request bills 20 s and renders 15 s (see decisionsForOwner).
 *   - resolution is '480p' when the value CONTAINS '480', else '720p' (price AND wire).
 *   - generate_audio: editor sends `enableAudio !== false` (fal default true, no audio surcharge);
 *     headless never sends it (fal renders audio). Hence editor-only legacy.wire, no SKU split.
 *   - aspect_ratio: text-to-video only, and on the editor only when the route's isTextToVideo is set.
 */
import type { ModelDescriptor, ModeSpec, Sku } from '../types';
import { IMG, END, W_IMG, W_END } from '../_shared';

const VERIFIED = '2026-09-19';

interface SeedanceSpec {
  id: string;
  displayName: string;
  ledgerName: string;
  slug: string;            // bytedance/<slug>/{text,image}-to-video
  rates: { '480p': number; '720p': number };
  usd: { '480p': number; '720p': number };
  usdNote: string;
  text: string;
  durationDefault: number; // node default (Seedance25Video '5', Seedance2Video '6'); see the header on why the engine default is 5
}

function seedance(s: SeedanceSpec): ModelDescriptor {
  const t2v: ModeSpec = { endpoint: `bytedance/${s.slug}/text-to-video`, ports: [], media: {} };
  const i2v: ModeSpec = { endpoint: `bytedance/${s.slug}/image-to-video`, ports: [IMG()], media: { image: W_IMG } };
  const flf: ModeSpec = {
    endpoint: `bytedance/${s.slug}/image-to-video`, ports: [IMG('input-image-first'), END()], media: { image: W_IMG, endImage: W_END },
  };
  const sku: Sku = {
    id: s.id,
    rate: { by: ['resolution'], table: { ...s.rates }, fallbackKey: '720p' },
    vendor: { usd: { ...s.usd }, per: 'second', field: 'pricingInfoOverride', source: `bytedance/${s.slug}/text-to-video (${s.usdNote})`,
      text: s.text, verified: VERIFIED },
    registry: { displayName: s.displayName, operationType: 'video', publishedTier: '720p', flatRate: false },
  };
  return {
    id: s.id, provider: 'falai', face: 'video', displayName: s.displayName, ledgerName: s.ledgerName, status: 'live',
    prompt: { field: 'prompt', required: true },
    modes: { t2v, i2v, flf },
    params: [
      { id: 'duration', type: 'int', min: 4, max: 15, uiValues: [4, 5, 6, 8, 10, 12, 15], default: 5, normalize: 'trim', role: 'units',
        // `Math.round(Number(d) || 5)` then clamp 4..15; 0 is falsy, so it becomes 5 (not the clamp's 4).
        aliases: { '0': 5 }, invalid: { coerce: { kind: 'clamp', min: 4, max: 15 } },
        wire: { field: 'duration', as: 'string' }, ui: { label: 'Duration', tier: 'basic' } },
      { id: 'resolution', type: 'enum', values: ['480p', '720p'], default: '720p', normalize: 'lower', role: 'tier',
        // `String(resolution || '720p').includes('480') ? '480p' : '720p'` for the price and the wire.
        aliases: { '480': '480p' }, invalid: { coerce: { kind: 'default' } },
        wire: { field: 'resolution' }, ui: { label: 'Resolution', tier: 'basic' } },
      { id: 'aspectRatio', type: 'enum', values: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'], default: '16:9', onlyModes: ['t2v'],
        // `if (isTextToVideo)` in the editor arm ($t2v: false on the route's t2v-endpoint fallbacks).
        sendWhen: { $t2v: { eq: true } },
        invalid: 'passthrough', wire: { field: 'aspect_ratio' }, ui: { label: 'Aspect ratio', tier: 'basic' } },
      // `enableAudio !== false`: only a literal false turns audio off; every other value (incl. the
      // coerced garbage) is on, which is exactly "out of domain -> default true".
      { id: 'enableAudio', type: 'bool', default: true, invalid: { coerce: { kind: 'default' } }, wire: false,
        ui: { label: 'Generate audio', tier: 'basic' } },
    ],
    legacy: {
      wire: [
        { field: 'generate_audio', fromParam: 'enableAudio', surface: 'editor',
          why: 'the editor arm always sends `enableAudio !== false`; headless never sends it (fal default true). Audio does not move the price on any 2.x tier.',
          retire: 'owner-approval' },
      ],
      omits: [
        { field: 'generate_audio', why: 'headless omits it: fal renders its default (audio on); no price effect' },
        { field: 'aspect_ratio', why: 'image-to-video / first-last-frame never send it (fal default "auto" follows the input image); no price effect' },
      ],
      notes: [
        `Node default duration is ${s.durationDefault}; the engine default is the route fallback 5 (\`Number(d) || 5\`), which is what a blank value renders and bills (R5 batch rule).`,
        'Editor t2v endpoint reached WITHOUT isTextToVideo (i2v + lastImage, FLF with one image, garbage mode) sends no aspect_ratio: not reproducible without the route flag (U6 residual).',
      ],
    },
    pricing: {
      unit: 'second', units: { param: 'duration', stage: 'raw' }, rounding: 'table-min1', policy: 'fal-video-35', skus: [sku],
    },
    knowledge: {
      summary: `${s.displayName}: 4-15 s at 480p or 720p; audio is native and free; first-last-frame via end_image_url.`,
    },
    schemaVerified: VERIFIED,
  };
}

export const SEEDANCE_25 = seedance({
  id: 'seedance-2.5', displayName: 'Seedance 2.5', ledgerName: 'Seedance 2.5', slug: 'seedance-2.5',
  rates: { '480p': 102, '720p': 219 }, usd: { '480p': 0.2205, '720p': 0.473 }, usdNote: 'pricingInfoOverride, per-second figures',
  text: 'For 480p, your request will cost roughly **$0.2205** per second of generated video, for 720p, you will be charged roughly **$0.4730** per second of generated video, and for 1080p, roughly **$1.164** per second of generated video.',
  durationDefault: 5,
});

export const SEEDANCE_2 = seedance({
  id: 'seedance-2', displayName: 'Seedance 2 Pro', ledgerName: 'Seedance 2 Pro', slug: 'seedance-2.0',
  // fal publishes no 480p per-second figure for 2.0 standard: 480p = 720p x 0.46617 (the token ratio
  // the 2.5 and mini tiers publish), as CreditService SEEDANCE_FAL_RATES documents.
  rates: { '480p': 66, '720p': 141 }, usd: { '480p': 0.14144, '720p': 0.3034 }, usdNote: 'pricingInfoOverride 720p; 480p derived by token ratio',
  text: 'For every second of 720p video you generated, you will be charged **$0.3034/second** and for 1080p you will be charged **$0.682/second**.',
  durationDefault: 6,
});

export const SEEDANCE_2_FAST = seedance({
  id: 'seedance-2-fast', displayName: 'Seedance 2 Fast', ledgerName: 'Seedance 2 Fast', slug: 'seedance-2.0/fast',
  rates: { '480p': 53, '720p': 112 }, usd: { '480p': 0.11277, '720p': 0.2419 }, usdNote: 'pricingInfoOverride 720p; 480p derived by token ratio',
  text: 'For every second of 720p video you generated, you will be charged **$0.2419/second**.',
  durationDefault: 6,
});

export const SEEDANCE_2_MINI = seedance({
  id: 'seedance-2-mini', displayName: 'Seedance 2 Mini', ledgerName: 'Seedance 2 Mini', slug: 'seedance-2.0/mini',
  rates: { '480p': 34, '720p': 72 }, usd: { '480p': 0.0721, '720p': 0.1547 }, usdNote: 'pricingInfoOverride, per-second figures',
  text: 'For every second of video generated, you will be charged roughly **$0.0721/second** for 480p and **$0.1547/second** for 720p.',
  durationDefault: 6,
});

export const SEEDANCE_MODELS: readonly ModelDescriptor[] = [SEEDANCE_25, SEEDANCE_2, SEEDANCE_2_FAST, SEEDANCE_2_MINI];
